import React, { useState, useEffect } from 'react'
import { DateRangePicker } from 'rsuite'
import 'rsuite/dist/rsuite.css'
import { useTranslation } from 'react-i18next'
import '../../i18n'
import './DateSelector.css'

interface DateSelectorProps {
  placeholder: string
  onDateChange: (dates: [Date, Date] | null) => void
  defaultValue?: [Date, Date]
  /** Hide floating label when a parent FormField already shows one. */
  hideLabel?: boolean
  /** Inclusive bounds from the parent project or plan. */
  minDate?: Date | null
  maxDate?: Date | null
}

function dayStamp(value: Date) {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime()
}

function fits(
  range: [Date, Date] | null,
  minStamp: number | null,
  maxStamp: number | null
) {
  if (!range?.[0] || !range?.[1]) return false
  const start = dayStamp(range[0])
  const end = dayStamp(range[1])
  if (Number.isNaN(start) || Number.isNaN(end)) return false
  if (minStamp != null && (start < minStamp || end < minStamp)) return false
  if (maxStamp != null && (start > maxStamp || end > maxStamp)) return false
  return true
}

const DateSelector: React.FC<DateSelectorProps> = ({
  placeholder,
  defaultValue,
  onDateChange,
  hideLabel = true,
  minDate,
  maxDate,
}) => {
  const { t } = useTranslation()
  const minStamp = minDate ? dayStamp(new Date(minDate)) : null
  const maxStamp = maxDate ? dayStamp(new Date(maxDate)) : null
  const defaultStart = defaultValue?.[0] ? dayStamp(new Date(defaultValue[0])) : null
  const defaultEnd = defaultValue?.[1] ? dayStamp(new Date(defaultValue[1])) : null
  const bounded = minStamp != null || maxStamp != null

  const [dateRange, setDateRange] = useState<[Date, Date] | null>(() => {
    if (!bounded) return defaultValue ?? [new Date(), new Date()]
    return null
  })

  useEffect(() => {
    setDateRange((current) => {
      if (defaultStart != null && defaultEnd != null) {
        const proposed: [Date, Date] = [new Date(defaultStart), new Date(defaultEnd)]
        if (fits(proposed, minStamp, maxStamp)) {
          if (
            current &&
            dayStamp(current[0]) === defaultStart &&
            dayStamp(current[1]) === defaultEnd
          ) {
            return current
          }
          return proposed
        }
      }
      if (current && fits(current, minStamp, maxStamp)) return current
      if (bounded) return null
      return current ?? [new Date(), new Date()]
    })
  }, [bounded, defaultStart, defaultEnd, minStamp, maxStamp])

  const handleDateChange = (value: [Date, Date] | null) => {
    if (value && !fits(value, minStamp, maxStamp)) return
    setDateRange(value)
    onDateChange(value)
  }

  const openOn = minStamp != null ? new Date(minStamp) : undefined

  return (
    <div className={`incyia-date${hideLabel ? ' incyia-date--nolabel' : ''}`}>
      {!hideLabel ? (
        <label className="incyia-date-label">
          {t('Start Date')} & {t('End Date')}
        </label>
      ) : null}
      <DateRangePicker
        key={bounded ? `${minStamp}-${maxStamp}` : 'open'}
        value={dateRange}
        onChange={handleDateChange}
        character=" → "
        placeholder={placeholder || t('actionCreate.sections.planning.dates')}
        cleanable
        editable={false}
        block
        className="incyia-date-picker"
        ranges={[]}
        defaultCalendarValue={openOn ? [openOn, maxStamp != null ? new Date(maxStamp) : openOn] : undefined}
        shouldDisableDate={(date) => {
          const stamp = dayStamp(date)
          if (minStamp != null && stamp < minStamp) return true
          if (maxStamp != null && stamp > maxStamp) return true
          return false
        }}
      />
    </div>
  )
}

export default DateSelector
